import { getSingleValueForTasField, setSingleValueForTasField, type FieldName } from './fields.ts';
import { buildSchoolFilter } from './build-school-filter.ts';
import type StoreService from 'ember-data/store';
import type TasApplicationModel from '../../models/tas-application.ts';
import type TASCourseModel from '../../models/tas-course.ts';
import type TasProgramInstanceModel from '../../models/tas-program-instance.ts';
import type TasUnifiedInstitutionModel from '../../models/tas-unified-institution.ts';

/**
 * Saves the courses begin/end date onto the application based on the program template's
 * lock status. Returns true if the date was newly applied, false if the locked field
 * already had a value.
 */
export function saveCoursesDate(
  date: string,
  application: TasApplicationModel,
  lockStatus: string
): boolean {
  const field: FieldName =
    lockStatus === 'COURSE_END_DATE' ? 'COURSES_END_DATE' : 'COURSES_BEGIN_DATE';

  const fieldValue = getSingleValueForTasField(field, application.fields);
  if (fieldValue) {
    return false;
  }
  setSingleValueForTasField(field, date, application.fields);
  return true;
}

/**
 * Looks up the previously selected school for a TAS program instance, if any is stored.
 * Returns undefined when no institution IDs are present or when the lookup fails.
 */
export async function loadSelectedSchool(
  programInstance: TasProgramInstanceModel,
  store: StoreService
): Promise<TasUnifiedInstitutionModel | undefined> {
  try {
    const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
    const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

    if (!tasInstitutionId && !dapipInstitutionId) {
      return undefined;
    }

    const filter = buildSchoolFilter(programInstance);
    const results = await store.query<TasUnifiedInstitutionModel>('tas-unified-institution', {
      // @ts-expect-error Ember Data query filter type mismatch
      filter,
      page: { limit: 1, offset: 0 },
    });
    return results[0];
  } catch (e) {
    console.error('Error attempting to load previously selected school', e);
    return undefined;
  }
}

/**
 * Rolls back any unsaved tas-course records in the store. Call from a route's
 * `deactivate()` hook so the cleanup runs when the user leaves the route.
 */
export function rollbackUnsavedCourses(store: StoreService): void {
  const courses = store.peekAll('tas-course') as TASCourseModel[];
  courses.filter((course) => course.isNew).forEach((course) => course.rollbackAttributes());
}
